import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  Banner,
  SkeletonBodyText,
  Modal,
  TextField,
  Divider,
  ChoiceList,
  Spinner,
} from '@shopify/polaris';
import {
  useTikTokConfig,
  useConfigureTikTok,
  useToggleTikTok,
  useTestTikTok,
  useDisconnectTikTok,
  useConnectTikTok,
  useTikTokAssets,
} from '../hooks/api';
import { useLanguage, useTranslation } from '../i18n/LanguageContext';
import { localeMap } from '../i18n/translations';
import { openOAuthWindow, navigateOAuth } from '../lib/auth';
import { useOAuthCallbackError } from '../hooks/useOAuthCallbackError';
import { GateBanner, type PageError } from '../components/GateBanner';

function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'connected':
      return <Badge tone="success">Connected</Badge>;
    case 'warning':
      return <Badge tone="warning">Warning</Badge>;
    case 'error':
      return <Badge tone="critical">Error</Badge>;
    case 'disabled':
      return <Badge>Disabled</Badge>;
    default:
      return <Badge>{status}</Badge>;
  }
}

export function TikTok() {
  const { data: config, isLoading } = useTikTokConfig();
  const configureMutation = useConfigureTikTok();
  const toggleMutation = useToggleTikTok();
  const testMutation = useTestTikTok();
  const disconnectMutation = useDisconnectTikTok();
  const { language } = useLanguage();
  const t = useTranslation();

  const connectMutation = useConnectTikTok();

  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [selectedPixel, setSelectedPixel] = useState('');
  const [pixelCode, setPixelCode] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [testEventCode, setTestEventCode] = useState('');
  const [testResult, setTestResult] = useState<{ success?: boolean; message?: string } | null>(null);
  const [configError, setConfigError] = useState<PageError>(null);
  const [actionError, setActionError] = useState<PageError>(null);
  // Surface OAuth-callback failures delivered via ?error= (e.g. partial consent).
  useOAuthCallbackError(setActionError);
  const [connectError, setConnectError] = useState<PageError>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthErr = params.get('error');
    if (oauthErr) {
      setConnectError(
        /access_denied|user_denied|cancell?ed/i.test(oauthErr)
          ? 'The TikTok connection was canceled. Nothing was changed - you can connect again anytime.'
          : `The TikTok connection could not be completed (${oauthErr.replace(/^[a-z0-9_]*?_/, '')}). Please try again.`
      );
    }
    if (params.get('step') === 'select-pixel' || params.get('tiktok') === 'connected') {
      setShowPicker(true);
    }
  }, []);

  const assetsQuery = useTikTokAssets(showPicker);

  const handleConnect = useCallback(async () => {
    setConnectError(null);
    const win = openOAuthWindow();
    try {
      const { url } = await connectMutation.mutateAsync();
      navigateOAuth(win, url);
    } catch (error: any) {
      win?.close();
      setConnectError(error?.message ? error : 'Could not start the TikTok connection.');
    }
  }, [connectMutation]);

  const closePicker = useCallback(() => {
    setShowPicker(false);
    setSelectedPixel('');
    const params = new URLSearchParams(window.location.search);
    params.delete('step');
    params.delete('tiktok');
    params.delete('platform');
    const clean = window.location.pathname + (params.toString() ? `?${params.toString()}` : '');
    window.history.replaceState({}, '', clean);
  }, []);

  // TikTok cannot mint the Events API token via OAuth, so picking a pixel just
  // pre-fills the pixel code; the merchant pastes the token in the config modal.
  const handlePickPixel = useCallback(() => {
    if (!selectedPixel) return;
    setPixelCode(selectedPixel);
    closePicker();
    setShowConfigModal(true);
  }, [selectedPixel, closePicker]);

  // A soft check on input that is obviously wrong, and nothing more. TikTok does
  // not document its pixel code as a stable pattern, so validating its shape
  // would risk rejecting a valid code, which is worse than not checking at all.
  // An internal space or a URL is always a bad paste, though. Trailing
  // whitespace is not flagged here because handleConfigure trims it silently.
  const pixelCodeWarning = useMemo(() => {
    const v = pixelCode.trim();
    if (!v) return undefined;
    if (/\s/.test(v)) return 'There is a space in the middle of this. Paste the pixel code on its own.';
    if (v.includes('://')) return 'That looks like a web address. Paste the pixel code, not the page URL.';
    return undefined;
  }, [pixelCode]);

  const handleConfigure = useCallback(async () => {
    setConfigError(null);
    // Both values are copied by hand out of TikTok's UI, so a trailing space or
    // a newline rides along more often than not. Untrimmed, it reaches the
    // Events API as part of the credential and every send fails authentication
    // with nothing on screen to explain why.
    const cleanPixel = pixelCode.trim();
    const cleanToken = accessToken.trim();
    if (!cleanPixel || !cleanToken) {
      setConfigError('Pixel Code and Access Token are required.');
      return;
    }

    try {
      await configureMutation.mutateAsync({
        pixelCode: cleanPixel,
        accessToken: cleanToken,
        testEventCode: testEventCode.trim() || undefined,
      });
      setShowConfigModal(false);
      setPixelCode('');
      setAccessToken('');
      setTestEventCode('');
    } catch (error: any) {
      setConfigError(error?.message ? error : t.common.configFailed);
    }
  }, [pixelCode, accessToken, testEventCode, configureMutation]);

  const handleToggle = useCallback(
    async (enabled: boolean) => {
      setActionError(null);
      try {
        await toggleMutation.mutateAsync(enabled);
      } catch (error: any) {
        console.error('Failed to toggle:', error);
        setActionError(error?.message ? error : t.common.actionFailed);
      }
    },
    [toggleMutation]
  );

  const handleTest = useCallback(async () => {
    setTestResult(null);
    setActionError(null);
    try {
      const result: any = await testMutation.mutateAsync();
      setTestResult({
        success: true,
        message: result.message || 'Test event sent successfully.',
      });
    } catch (error: any) {
      setTestResult({ success: false, message: error.message || 'Test event failed.' });
    }
  }, [testMutation]);

  const handleDisconnect = useCallback(async () => {
    setActionError(null);
    try {
      await disconnectMutation.mutateAsync();
      setShowDisconnectModal(false);
    } catch (error: any) {
      console.error('Failed to disconnect:', error);
      setActionError(error?.message ? error : t.common.actionFailed);
    }
  }, [disconnectMutation]);

  if (isLoading) {
    return (
      <Page title="TikTok Events API">
        <Layout>
          <Layout.Section>
            <Card>
              <SkeletonBodyText lines={6} />
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title="TikTok Events API"
      subtitle="Server-side conversion tracking for TikTok Ads"
      primaryAction={
        config?.connected
          ? {
              content: config.isEnabled ? 'Disable Tracking' : 'Enable Tracking',
              onAction: () => handleToggle(!config.isEnabled),
              loading: toggleMutation.isPending,
            }
          : undefined
      }
    >
      <Layout>
        {/* Action Error Banner */}
        {actionError && (
          <Layout.Section>
            <GateBanner error={actionError} onDismiss={() => setActionError(null)} />
          </Layout.Section>
        )}

        {/* Connect Error Banner */}
        {connectError && (
          <Layout.Section>
            <GateBanner error={connectError} onDismiss={() => setConnectError(null)} />
          </Layout.Section>
        )}

        {/* Error Banner */}
        {config?.lastErrorMessage && (
          <Layout.Section>
            <Banner tone="warning" title="Warning">
              <p>{config.lastErrorMessage}</p>
            </Banner>
          </Layout.Section>
        )}

        {/* Test Result */}
        {testResult && (
          <Layout.Section>
            <Banner
              tone={testResult.success ? 'success' : 'critical'}
              onDismiss={() => setTestResult(null)}
            >
              <p>{testResult.message}</p>
            </Banner>
          </Layout.Section>
        )}

        {/* Connection Status */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="h2" variant="headingMd">
                  Connection Status
                </Text>
                {config?.connected && <StatusBadge status={config.status || 'connected'} />}
              </InlineStack>

              {config?.connected ? (
                <BlockStack gap="300">
                  <InlineStack gap="200">
                    <Text as="span" variant="bodyMd" tone="subdued">
                      Pixel Code:
                    </Text>
                    <Text as="span" variant="bodyMd" fontWeight="semibold">
                      {config.pixelCode}
                    </Text>
                  </InlineStack>

                  {config.testEventCode && (
                    <InlineStack gap="200">
                      <Text as="span" variant="bodyMd" tone="subdued">
                        Test Event Code:
                      </Text>
                      <Text as="span" variant="bodyMd" fontWeight="semibold">
                        {config.testEventCode}
                      </Text>
                    </InlineStack>
                  )}

                  {config.lastSyncAt && (
                    <InlineStack gap="200">
                      <Text as="span" variant="bodyMd" tone="subdued">
                        Last Sync:
                      </Text>
                      <Text as="span" variant="bodyMd">
                        {new Date(config.lastSyncAt).toLocaleString(localeMap[language])}
                      </Text>
                    </InlineStack>
                  )}

                  <Divider />

                  <InlineStack gap="200">
                    <Button onClick={handleTest} loading={testMutation.isPending}>
                      Send Test Event
                    </Button>
                    <Button onClick={() => setShowConfigModal(true)}>
                      Update Configuration
                    </Button>
                    <Button
                      variant="plain"
                      tone="critical"
                      onClick={() => setShowDisconnectModal(true)}
                    >
                      Disconnect
                    </Button>
                  </InlineStack>
                </BlockStack>
              ) : (
                <BlockStack gap="300">
                  <Text as="p" variant="bodyMd">
                    {/*
                      Short on purpose: the card below explains the how and the
                      why in full, and saying it twice on one screen made the
                      channel read as more work than it is.
                    */}
                    {config?.oauthAvailable
                      ? 'Connect TikTok to pick your pixel automatically. You will still generate the Events API token yourself, which is the one step no authorization flow can do.'
                      : 'No pixel connected yet. It takes one visit to TikTok, and the steps are below.'}
                  </Text>
                  <InlineStack gap="200">
                    {config?.oauthAvailable ? (
                      <>
                        <Button variant="primary" onClick={handleConnect} loading={connectMutation.isPending}>
                          Connect TikTok
                        </Button>
                        <Button onClick={() => setShowConfigModal(true)}>
                          Advanced: enter manually
                        </Button>
                      </>
                    ) : (
                      <Button variant="primary" onClick={() => setShowConfigModal(true)}>
                        Set up TikTok
                      </Button>
                    )}
                  </InlineStack>
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/*
          Setup Instructions.

          Rewritten as ONE trip, because that is what it is. The old version read
          as two errands - copy the pixel code, then go and generate a token -
          which is what made this channel feel heavier than the others. Both
          values live on the SAME screen: your pixel's Settings tab, with the
          pixel code at the top and Generate Access Token under Events API.

          That single fact is also why there is no one-click connect here, and
          why building the OAuth app was not worth a multi-week review: it would
          have saved the merchant one copy-paste on a page they must open
          anyway. Say the limitation plainly instead of leaving them hunting for
          a Connect button that does not exist.
        */}
        {!config?.connected && (
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">
                  Setting up TikTok
                </Text>

                <Banner tone="info">
                  <p>
                    TikTok is the one channel that needs a pasted token. Its Events API token is
                    generated on your pixel's own settings page and cannot be issued by an
                    authorization flow, the way Google or Meta do it. The good news: the pixel code
                    is on that same page, so this is one trip, not two.
                  </p>
                </Banner>

                <BlockStack gap="200">
                  <Text as="p" variant="bodyMd">
                    1.{' '}
                    <a href="https://ads.tiktok.com/i18n/events_manager" target="_blank" rel="noopener noreferrer">
                      Open TikTok Events Manager
                    </a>
                    , then Assets &gt; Events &gt; Web Events &gt; Manage, and select your pixel.
                  </Text>
                  <Text as="p" variant="bodyMd">
                    2. Open its <b>Settings</b> tab. Everything below is on this one screen.
                  </Text>
                  <Text as="p" variant="bodyMd">
                    3. Copy the <b>Pixel Code</b> at the top (it looks like CXXXXXXXXXXXXXXXXX).
                  </Text>
                  <Text as="p" variant="bodyMd">
                    4. Scroll to <b>Events API</b> and click <b>Generate Access Token</b>. Copy it
                    straight away: TikTok hides it again as soon as the page reloads.
                  </Text>
                  <Text as="p" variant="bodyMd">
                    5. Paste both here, then send a test event. It should appear in the same Events
                    Manager within seconds.
                  </Text>
                </BlockStack>
              </BlockStack>
            </Card>
          </Layout.Section>
        )}

        {/* Configuration Modal */}
        <Modal
          open={showConfigModal}
          onClose={() => {
            setShowConfigModal(false);
            setConfigError(null);
          }}
          title="Configure TikTok Events API"
          primaryAction={{
            content: 'Save Configuration',
            onAction: handleConfigure,
            loading: configureMutation.isPending,
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: () => {
                setShowConfigModal(false);
                setConfigError(null);
              },
            },
          ]}
        >
          <Modal.Section>
            <BlockStack gap="400">
              {configError && (
                <GateBanner error={configError} />
              )}

              <TextField
                label="Pixel Code"
                value={pixelCode}
                onChange={setPixelCode}
                placeholder="CXXXXXXXXXXXXXXXXX"
                autoComplete="off"
                helpText="At the top of your pixel's Settings tab, in TikTok Events Manager."
                error={pixelCodeWarning}
              />

              <Banner tone="info">
                <p>
                  The token is on that same Settings tab.{' '}
                  <a href="https://ads.tiktok.com/i18n/events_manager" target="_blank" rel="noopener noreferrer">
                    Open TikTok Events Manager
                  </a>
                  , go to your pixel &gt; Settings &gt; Events API &gt; Generate Access Token, and copy
                  it right away: TikTok hides it again once the page reloads.
                </p>
              </Banner>

              <TextField
                label="Access Token"
                value={accessToken}
                onChange={setAccessToken}
                type="password"
                placeholder="Enter your Events API access token"
                autoComplete="off"
                helpText="Same screen, under Events API. Shown once, so copy it before reloading."
              />

              <TextField
                label="Test Event Code (optional)"
                value={testEventCode}
                onChange={setTestEventCode}
                placeholder="e.g., TEST12345"
                autoComplete="off"
                helpText="Use a test event code from TikTok Events Manager to verify events without affecting production data."
              />
            </BlockStack>
          </Modal.Section>
        </Modal>

        {/* Pixel Picker Modal (after one-click Connect) */}
        <Modal
          open={showPicker}
          onClose={closePicker}
          title="Choose your TikTok pixel"
          primaryAction={{
            content: 'Continue',
            onAction: handlePickPixel,
            disabled: !selectedPixel,
          }}
          secondaryActions={[{ content: 'Cancel', onAction: closePicker }]}
        >
          <Modal.Section>
            {assetsQuery.isLoading ? (
              <InlineStack gap="200" blockAlign="center">
                <Spinner size="small" accessibilityLabel="Loading pixels" />
                <Text as="span" variant="bodyMd">Loading your TikTok pixels...</Text>
              </InlineStack>
            ) : assetsQuery.isError ? (
              <Banner tone="critical">
                <p>Could not load your pixels. The connection may have expired - click Connect again.</p>
              </Banner>
            ) : (assetsQuery.data?.pixels.length ?? 0) === 0 ? (
              <Banner tone="warning">
                <p>No TikTok pixels found on this account. Create one in TikTok Events Manager, then reconnect.</p>
              </Banner>
            ) : (
              <BlockStack gap="300">
                <ChoiceList
                  title="Pick your pixel. Next, you will paste the Events API token for it."
                  choices={(assetsQuery.data?.pixels ?? []).map((p) => ({
                    label: `${p.name} (${p.advertiserName})`,
                    value: p.pixelCode,
                  }))}
                  selected={selectedPixel ? [selectedPixel] : []}
                  onChange={(v) => setSelectedPixel(v[0] ?? '')}
                />
              </BlockStack>
            )}
          </Modal.Section>
        </Modal>

        {/* Disconnect Confirmation Modal */}
        <Modal
          open={showDisconnectModal}
          onClose={() => setShowDisconnectModal(false)}
          title="Disconnect TikTok Events API"
          primaryAction={{
            content: 'Disconnect',
            onAction: handleDisconnect,
            loading: disconnectMutation.isPending,
            destructive: true,
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: () => setShowDisconnectModal(false),
            },
          ]}
        >
          <Modal.Section>
            <Text as="p" variant="bodyMd">
              Are you sure you want to disconnect TikTok Events API? Server-side tracking will stop
              immediately and conversion data will no longer be sent to TikTok Ads.
            </Text>
          </Modal.Section>
        </Modal>
      </Layout>
    </Page>
  );
}
